import { useEffect, useMemo, useState } from "react";

import type { AdminApi } from "@/api";
import { modelLabel } from "@/components/batch-test/teSpec";
import { Banner, Card, CardHead, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { MatrixPage, PagelessRunListResponse, PagelessRunRow, TeBaseline } from "@/types";
import { compactTime } from "@/utils/format";
import { formatScore } from "@/utils/rubric";
import { matrixColumns } from "./matrix";
import { MatrixTable } from "./MatrixTable";

/** Rows per request; the picker follows `next_cursor` until every run is read. */
const RUN_LIMIT = 100;

const SELECT =
  "rounded-md border border-border bg-surface-input px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-60";

const NO_DIMMED: ReadonlySet<string> = new Set();

/** `schedule` is what MCP `run_batch` and a scheduled run record. */
const TRIGGER_LABEL: Record<string, string> = { schedule: "MCP·예약", manual: "수동" };

function errorText(err: unknown) {
  return err instanceof Error ? err.message : String(err);
}

/** `10-30 14:02 · MCP·예약 · 모델 X · 12 TC · 평균 0.72` — one run in the picker. */
function runOptionLabel(r: PagelessRunRow, baseline: TeBaseline | null): string {
  const model = r.axes.llm_model ?? baseline?.default_model ?? null;
  return [
    compactTime(r.created_at),
    TRIGGER_LABEL[r.trigger] ?? r.trigger,
    `모델 ${modelLabel(model, baseline)}`,
    `${r.test_case_count} TC`,
    r.status === "running" ? "실행 중" : `평균 ${r.avg != null ? formatScore(r.avg) : "—"}`,
  ].join(" · ");
}

/**
 * Batches that ran on no Batch Test page — MCP `run_batch` and scheduled
 * runs (`page_id` NULL). The page picker above never reaches them, so each
 * is shown here on its own as a one-column Matrix; a cell opens the same
 * Test Result a page's cell does. Hidden while there are none.
 */
export function PagelessRuns({
  api,
  baseline,
  reloadKey,
  onCell,
}: {
  api: AdminApi;
  baseline: TeBaseline | null;
  reloadKey: number;
  onCell: (runId: string, testCaseId: string) => void;
}) {
  const [rows, setRows] = useState<PagelessRunRow[] | null>(null);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ id: string; page?: MatrixPage; error?: string } | null>(null);

  useEffect(() => {
    let alive = true;
    setError("");
    (async () => {
      const all: PagelessRunRow[] = [];
      let cursor: string | null = null;
      do {
        const list: PagelessRunListResponse = await api.listPagelessRuns({ cursor, limit: RUN_LIMIT });
        all.push(...list.items);
        cursor = list.next_cursor;
      } while (cursor && alive);
      return all;
    })()
      .then((all) => {
        if (!alive) return;
        setRows(all);
        setError("");
      })
      .catch((err) => alive && setError(errorText(err)));
    return () => {
      alive = false;
    };
  }, [api, reloadKey]);

  const chosenId =
    (picked && rows?.some((r) => r.batch_run_id === picked) ? picked : null) ??
    rows?.[0]?.batch_run_id ??
    null;

  useEffect(() => {
    if (!chosenId) return;
    let alive = true;
    setDetail({ id: chosenId });
    api
      .getBatchRunMatrix(chosenId)
      .then((page) => alive && setDetail({ id: chosenId, page }))
      .catch((err) => alive && setDetail({ id: chosenId, error: errorText(err) }));
    return () => {
      alive = false;
    };
  }, [api, chosenId, reloadKey]);

  const page = detail?.id === chosenId ? detail.page : undefined;
  const pageError = detail?.id === chosenId ? detail.error : undefined;
  const columns = useMemo(() => (page ? matrixColumns(page) : []), [page]);

  if (!error && rows !== null && rows.length === 0) return null;

  return (
    <section aria-label="페이지 없는 실행" className="min-w-0">
      <Card className="min-w-0 overflow-hidden">
        <CardHead title="페이지 없는 실행" hint="MCP run_batch · 예약 실행 — Batch Test 페이지 없이 돈 배치">
          <span className="text-xs text-muted-foreground tabular-nums">{rows ? `${rows.length} 건` : ""}</span>
        </CardHead>
        {error ? (
          <div className="p-4">
            <Banner tone="error">{error}</Banner>
          </div>
        ) : rows === null ? (
          <div className="p-4">
            <Skeleton className="h-10" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-4 py-3 text-xs">
              <span className="text-muted-foreground">실행</span>
              <select
                aria-label="표시할 페이지 없는 실행"
                className={cn(SELECT, "max-w-full")}
                value={chosenId ?? ""}
                onChange={(e) => setPicked(e.target.value || null)}
              >
                {rows.map((r) => (
                  <option key={r.batch_run_id} value={r.batch_run_id}>
                    {runOptionLabel(r, baseline)}
                  </option>
                ))}
              </select>
              {page && (
                <span className="text-muted-foreground">
                  {[page.created_by_email ?? "—", `${page.test_case_ids.length} TC`].join("  ·  ")}
                </span>
              )}
            </div>
            {pageError ? (
              <div className="p-4">
                <Banner tone="error">{pageError}</Banner>
              </div>
            ) : !page ? (
              <div className="grid gap-2 p-4">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : (
              <MatrixTable
                page={page}
                columns={columns}
                baseline={baseline}
                selectedTc={null}
                dimmed={NO_DIMMED}
                // One column: a row opens its only cell.
                onRow={(tc) => chosenId && onCell(chosenId, tc)}
                onCell={(column, tc) => onCell(column.run.batch_run_id, tc)}
              />
            )}
          </>
        )}
      </Card>
    </section>
  );
}
